import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import LocaleModal from "@/components/LocaleModal";
import SignInModal from "@/components/SignInModal";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "./ui/sheet";
import { supabase } from "@/lib/supabaseClient"; // adjust path if different
import { getSessionAndMemberStatus } from "@/lib/authz";
import { ArrowRight, Calendar, Calendar1, CircleUser, CircleUserRound, Globe, Languages, Menu, ShoppingBag, ShoppingBasket, ShoppingCart, Sparkles, User } from "lucide-react";


/** Hook: hide on scroll down, show on scroll up */
function useSmartHeader({ upThreshold = 6, minScrollToActivate = 80 } = {}) {
  const lastY = useRef(0);
  const raf = useRef(0);
  const [isFloating, setIsFloating] = useState(false); // fixed vs normal
  const [hidden, setHidden] = useState(false);         // slide out/in

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onScroll = () => {
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        const delta = y - lastY.current;

        if (Math.abs(delta) > upThreshold) {
          const goingDown = delta > 0;

          // Show header (fixed) after user scrolled a bit and is going up
          if (!goingDown && y > minScrollToActivate) {
            setIsFloating(true);
            setHidden(false); // slide in
          } else if (goingDown) {
            // On down, keep fixed but slide away
            setIsFloating(true);
            setHidden(true);  // slide out
          }

          // Near top → reset to normal flow
          if (y <= 0) {
            setIsFloating(false);
            setHidden(false);
          }

          lastY.current = y;
        }
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [upThreshold, minScrollToActivate]);

  return { isFloating, hidden };
}

function Header() {
  const navigate = useNavigate();

  // Sheet state
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Menu state
  const [activeMenu, setActiveMenu] = useState("main");
  const [subMenuItems, setSubMenuItems] = useState([]);
  const [titlePath, setTitlePath] = useState(["Palais Antoia"]); {/*issues with menu*/ }
  const resetMenuState = () => {
    setActiveMenu("main");
    setSubMenuItems([]);
    setTitlePath(["Palais Antoia"]);
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
    resetMenuState();
  };

  // Smart header
  const { isFloating, hidden } = useSmartHeader({
    upThreshold: 6,
    minScrollToActivate: 80,
  });

  // Auth gate (session + anon + membership)
  const [authGate, setAuthGate] = useState({
    session: null,
    isAnon: false,
    isMember: false,
  });

  useEffect(() => {
    let mounted = true;

    const refresh = async () => {
      try {
        const gate = await getSessionAndMemberStatus();
        if (!mounted) return;
        setAuthGate(gate);
      } catch (e) {
        console.error(e);
        if (!mounted) return;
        setAuthGate({ session: null, isAnon: false, isMember: false });
      }
    };

    refresh();

    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      refresh();
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const isMemberAuthed = !!authGate.session && authGate.isMember && !authGate.isAnon;


  // Measure header height for spacer
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);

  useEffect(() => {
    if (!headerRef.current) return;
    const el = headerRef.current;

    const measure = () => setHeaderH(el.offsetHeight || 0);
    measure();

    // Update on resize / content changes
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("load", measure);
    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      window.removeEventListener("load", measure);
      window.removeEventListener("resize", measure);
    };
  }, []);
  // Example data with sub menus (kept as you had it)
  const menuGroups = [
    /*     {
          items: [{ label: "Shop all →", link: "/productoverview" }],
        }, */
    /*     {
          items: [
            {
              label: "Men",
              subMenu: [
                { label: "All", link: "/productoverview" },
                { label: "Product", link: "/product" },
                { label: "Category", link: "/category" },
              ],
            },
            {
              label: "Women",
              subMenu: [
                { label: "All", link: "/category" },
                { label: "Clothing", link: "/category" },
                { label: "Accessories", link: "/category" },
                { label: "Handbags", link: "/category" },
                { label: "Jewellery", link: "/category" },
              ],
            },
            {
              label: "Heirlooms",
              subMenu: [
                { label: "All", link: "/category" },
                { label: "Clothing", link: "/category" },
                { label: "Accessories", link: "/category" },
                { label: "Handbags", link: "/category" },
                { label: "Jewellery", link: "/category" },
              ],
            },
            {
              label: "Home",
              subMenu: [
                { label: "All", link: "/category" },
                { label: "Clothing", link: "/category" },
                { label: "Accessories", link: "/category" },
                { label: "Handbags", link: "/category" },
                { label: "Jewellery", link: "/category" },
              ],
            },
            { label: "Gifts", link: "/productoverview" },
          ],
        }, */
        {
          items: [
            { label: "Store", link: "/store"
            /* subMenu: [
              { label: "All", link: "/category" },
              { label: "Fashion", link: "/category" },
              { label: "Accessories", link: "/category" },
              { label: "Jewellery", link: "/category" },
              { label: "Blankets", link: "/category" },
            ], */ },
          ],
        },
    {
      items: [
/*         { label: "Discover",
        subMenu: [

          { label: "Fashion", link: "/discover/fashion" },
          { label: "Jewellery", link: "/" },
          { label: "Interior", link: "/" },
          { label: "Wearable stories", link: "/discover/wearable-stories" },
          { label: "Drops", link: "/" },
        ],}, */
        /* { label: "How it works", link: "/howitworks" }, */
        { label: "Wearable stories", link: "/discover/wearable-stories" },
        { label: "Events", link: "/events" },
        { label: "Our story", link: "/about" },
        /* { label: "Journal", link: "/" }, */
      ],
    },
  ];

  const handleMainItemClick = (item) => {
    if (item.subMenu) {
      setActiveMenu("sub");
      setSubMenuItems(item.subMenu);
      setTitlePath((prev) => [...prev, item.label]);
    } else if (item.link) {
      navigate(item.link);
      setIsSheetOpen(false);
    }
  };

  const handleBackToMain = () => {
    setActiveMenu("main");
    setSubMenuItems([]);
    setTitlePath(["Palais Antoia"]);
  };

  // Keep header visible while sheet is open (optional UX choice)
  const actuallyHidden = isSheetOpen ? false : hidden;

  return (
    <>
      {/*     <Announcement/> */}

      {/* Spacer to prevent layout jump when header is fixed */}
      {isFloating && <div style={{ height: headerH }} />}

      <header
        ref={headerRef}
        className={[
          isFloating ? "fixed top-0 left-0 right-0" : "static",

          // ✅ mobile = flex, desktop = grid
          "z-30 w-full p-2 pt-[0.65rem] bg-background/80 backdrop-blur-xl border-b-2 border-white/40",
/*           "flex items-start justify-between gap-3",
          "lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-start", */
          "grid grid-cols-[1fr_auto_1fr] items-center gap-3",

          "transform transition-transform duration-300 will-change-transform",
          actuallyHidden ? "-translate-y-full" : "translate-y-0",
          "text-[rgb(var(--foreground))]",
        ].join(" ")}
      >
        {/* Left */}
        <div className="flex items-center my-auto lg:gap-2 gap-4 lg:justify-start">
          {/* Menu button */}
          <div className="flex items-center justify-center overflow-visible cursor-pointer pl-2 py-0">
          <Sheet
  open={isSheetOpen}
  onOpenChange={(open) => {
    setIsSheetOpen(open);
    if (!open) resetMenuState();
  }}
>
              {/* <SheetTrigger asChild>
                <div className="flex items-center focus:outline-none focus:ring-0 justify-center overflow-visible cursor-pointer pl-2 py-5 shrink-0">
                  <img
                    src="/icons/menu_icon.svg"
                    alt="Menu icon"
                    className="h-6 p-0 shrink-0"
                  />
                </div>
              </SheetTrigger> */}

              <SheetContent
                side="left"
                className="
                rounded-3xl
                inset-2
                bottom-2 right-2
                w-auto          /* 👈 mobile: almost full width */
                md:w-[480px]       /* 👈 desktop */
                h-auto
                
                bg-[rgb(var(--card))]
                text-[rgb(var(--card-foreground))]
                flex flex-col"
              >

                <SheetHeader>
                  <SheetTitle className="font-kalice text-3xl focus:outline-none focus:ring-0 focus-visible:ring-0 text-left">
                    {titlePath.map((part, i) => {
                      const isLast = i === titlePath.length - 1;
                      const isMenu = part === "Palais Antoia" && i === 0;
                      if (isMenu && !isLast) {
                        return (
                          <Button
                            key={i}
                            onClick={handleBackToMain}
                            variant="secondary"
                            className="items-end focus:outline-none focus:ring-0 font-kalice text-3xl text-gray-400 mr-1 hover:text-black bg-transparent px-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                          >
                            {part} {" → "}
                          </Button>
                        );
                      } else if (!isLast) {
                        return (
                          <Button
                            variant="secondary"
                            className="p-12">
                            <span key={i} className="focus:outline-none focus:ring-0 text-gray-400 mr-1 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0">
                              {part} &gt;{" "}
                            </span>
                          </Button>
                        );
                      }
                      return <Button
                        variant="secondary"
                        className="mb-4 cursor-default focus:outline-none focus:ring-0 font-kalice text-3xl mr-1 text-black bg-transparent px-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0">
                        <span key={i} className="">{part}</span> </Button>;
                    })}
                  </SheetTitle>




                  <Separator className="my-0" />
                </SheetHeader>


                <div className="flex-1 overflow-y-auto font-sans font-medium relative">
  <AnimatePresence mode="wait">
    {activeMenu === "main" ? (
      <motion.div
        key="main-menu"
        initial={{ opacity: 0, x: -14 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 14 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="my-0"
      >
        {menuGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="mb-0">
            <ul className="space-y-2">
              {group.items.map((item, idx) => {
                if (item.subMenu) {
                  return (
                    <li key={idx}>
                      <Button
                        variant="link"
                        onClick={() => handleMainItemClick(item)}
                        className="focus:outline-none focus:ring-0 hover:no-underline text-sm bg-transparent text-gray-700 hover:text-black px-0 p-0"
                      >
                        {item.label}
                      </Button>
                    </li>
                  );
                } else if (item.link) {
                  return (
                    <li key={idx}>
                      <Button
                        variant="link"
                        onClick={() => {
                          navigate(item.link);
                          closeSheet();
                        }}
                        className="focus:outline-none focus:ring-0 hover:no-underline text-sm font-sans bg-transparent text-gray-700 hover:text-black px-0 p-0"
                      >
                        {item.label}
                      </Button>
                    </li>
                  );
                }
                return <li key={idx}>{item.label}</li>;
              })}
            </ul>

            {groupIndex < menuGroups.length - 1 && (
              <Separator className="my-4" />
            )}
          </div>
        ))}
      </motion.div>
    ) : (
      <motion.div
        key="sub-menu"
        initial={{ opacity: 0, x: 14 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -14 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        <ul className="font-sans font-medium text-sm my-0 space-y-2">
          {subMenuItems.map((item, idx) => {
            if (item.subMenu) {
              return (
                <li key={idx}>
                  <Button
                    variant="link"
                    onClick={() => handleMainItemClick(item)}
                    className="focus:outline-none focus:ring-0 hover:no-underline text-sm font-sans bg-transparent text-gray-700 hover:text-black px-0 p-0"
                  >
                    {item.label}
                  </Button>
                </li>
              );
            } else if (item.link) {
              return (
                <li key={idx}>
                  <Button
                    variant="link"
                    onClick={() => {
                      navigate(item.link);
                      closeSheet();
                    }}
                    className="focus:outline-none focus:ring-0 hover:no-underline text-sm font-sans bg-transparent text-gray-700 hover:text-black px-0 p-0"
                  >
                    {item.label}
                  </Button>
                </li>
              );
            }
            return <li key={idx}>{item.label}</li>;
          })}
        </ul>
      </motion.div>
    )}
  </AnimatePresence>
</div>


                <SheetFooter className="p-0">
                  <div className="w-full space-y-2">

                    <div>
                      <Separator className="mb-4 mt-1" />
                    </div>


                    <LocaleModal
                      trigger={
                        <Button
                          variant="default"
                          className="text-sm font-sans rounded bg-transparent hover:bg-transparent text-black/60 hover:text-black w-fit h-fit p-0"
                        >
                          <Globe className="w-4 h-4 mr-2" />English
                        </Button>
                      }

                      onApply={({ region }) => {
                        window.location.pathname = `/${region}`;
                      }}

                    />


                    <div>
                      <Separator className="my-4" />
                    </div>

                    <Button
                      variant="default"
                      className="w-full text-sm font-sans rounded"
                      onClick={() => {
                        setIsSheetOpen(false);   // 👈 close sheet
                        navigate("/bookings"); // 👈 navigate
                      }}
                    >
                      <Calendar className="w-4 h-4 mr-2" /> Book an appointment
                    </Button>

                    {isMemberAuthed ? (
                      <Button
                        variant="default"
                        className="w-full text-sm font-sans rounded flex items-center"
                        onClick={() => {
                          setIsSheetOpen(false);
                          navigate("/members");
                        }}
                      >
                        <CircleUser className="w-4 h-4 mr-2" /> Account
                      </Button>
                    ) : (
                      <SignInModal
                        trigger={
                          <Button variant="default" className="w-full text-sm font-sans rounded">
                            <CircleUser className="w-4 h-4 mr-2" /> Sign in
                          </Button>
                        }
                        onSignIn={async ({ email, password }) => { }}
                        onForgotPassword={() => { }}
                        onCreateAccount={() => { }}
                        showSocial={false}
                      />
                    )}



                  </div>

                </SheetFooter>


              </SheetContent>
            </Sheet>
          </div>


          {/* Create in Store */}
{/*           <Link
            to="/store"
            className="hidden lg:flex items-center gap-2 font-sans font-medium text-sm text-black/80 hover:text-black transition hover:bg-white/60 rounded-2xl px-4 ml-4 py-5 relative"
            aria-label="Store"
          >
            <Sparkles className="h-4 w-4" />
            <span className="">Create in Store</span>
          </Link> */}

          {/* CTA */}
          {/*                       <Link
              to="/bookings"
              className="hidden md:flex items-center gap-2 text-white  transition bg-black hover:bg-black/90 px-4 py-5 rounded-2xl font-sans font-medium text-sm"
            >
              <Sparkles className="h-4 w-4" />
              <span className="">Create in store</span>
              <span aria-hidden>→</span>
            </Link> */}

          {/* ✅ Logo next to menu on mobile only */}

          {/*           <Link to="/" className="lg:hidden shrink-0">
            <img src="/logos/C_Glyph.svg" alt="Cypherloom Logo" className="h-14 shrink-0" />
          </Link> */}
        </div>

        {/*         <Link to="/" className="lg:hidden shrink-0">
            <img src="/logos/C_Glyph.svg" alt="Cypherloom Logo" className="h-14 shrink-0" />
          </Link> */}

        {/* Center (desktop only) */}
        <div className="flex items-center my-auto justify-center overflow-visible shrink-0 px-2">
          <Link to="/">
            <img src="/logos/C_Glyph.svg" alt="Cypherloom Logo" className="h-14" />
          </Link>
        </div>

{/*         <div className="flex items-center my-auto justify-center overflow-visible shrink-0 px-2">
          <Link to="/">
            <img src="/logos/palais_logo.svg" alt="Cypherloom Logo" className="h-10" />
          </Link>
        </div> */}

        {/* Right: Utility buttons */}

        <div className="flex items-center justify-end gap-2">

          {/* Language */}
          {/*           <LocaleModal
            trigger={
              <button
                className="hidden text-black/80 hover:text-black font-sans font-medium text-sm md:flex items-center transition hover:bg-white/60 rounded-2xl px-4 py-5 gap-2">
                <Globe className="w-4 h-4" /> <span className="">EN</span>
              </button>
            }
            onApply={({ region }) => {
              window.location.pathname = `/${region}`;
            }}
          /> */}

          {/* <div className="flex items-center gap-2 font-sans text-sm tracking-wide bg-white/60 border-2 border-white rounded-[1.4rem] px-2 py-2"> */}
          <div className="flex items-center gap-2 font-sans text-sm tracking-wide  px-2 py-2">

            {/* Cart */}
{/*             <button
              type="button"
              className="snipcart-checkout flex items-center gap-2 text-black/80 hover:text-black transition hover:bg-white rounded-2xl px-4 py-5 relative"
              aria-label="Cart"
            >
              <ShoppingBasket className="h-4 w-4" />
              <span className="hidden md:inline">Cart</span>
            </button> */}


            {/* Account: keep as icon, or move into menu */}
            {isMemberAuthed ? (
              <button
                type="button"
                onClick={() => navigate("/members")}
                className="flex items-center gap-2 text-black/80 hover:text-black transition hover:bg-white rounded-2xl px-4 py-5"
                aria-label="Account"
              >
                <CircleUser className="h-4 w-4" />
                <span className="hidden md:inline">Account</span>
              </button>
            ) : (
              <SignInModal
                trigger={
                  <button
                    type="button"
                    className="flex items-center gap-2 text-black/80 hover:text-black transition hover:bg-white rounded-2xl px-4 py-5"
                  >
                    <CircleUser className="h-4 w-4" />
                    <span className="hidden md:inline">Sign in</span>
                  </button>
                }
                onSignIn={async () => { }}
                onForgotPassword={() => { }}
                onCreateAccount={() => { }}
                showSocial={false}
              />
            )}

            {/* CTA */}
    {/*         <Link
              to="/bookings"
              className="hidden min-[340px]:flex items-center gap-2 text-white  transition bg-black hover:bg-black/90 px-4 py-5 rounded-2xl"
            >
              <Calendar className="h-4 w-4" />
              <span className="hidden min-[440px]:inline">Book</span>
              <span aria-hidden>→</span>
            </Link> */}
          </div>
        </div>


      </header>
    </>
  );
}

export default Header;
